import { UserRegistryApiService } from './user-registry-api.service';
import { NotificationPreferencesApiService } from './notification-preferences-api.service';
import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import {
  forkJoin,
  map,
  of,
  switchMap,
  Observable,
  catchError,
  throwError,
  share,
} from 'rxjs';
import { IGridRequestBody, IGridResponseBody } from 'mt-ui-lib/interfaces';
import { ISelectItem } from 'ngx-mt-ui-lib/interfaces';
import { KeycloakSessionService } from '../services/auth/keycloak-session.service';
import { IUser, IUserGridItem, IUserUpdateParams } from '../interfaces/user';
import {
  KeycloakUser,
  UserSession,
  UserCredential,
  UserLoginFailure,
  UserDeletion,
  TemporaryPassword,
  UserLoginEvent,
} from '../interfaces/keycloak-user';
import { EmailStatus } from '../interfaces/keycloak-user';

@Injectable({ providedIn: 'root' })
export class UserApiService {
  private readonly registry = inject(UserRegistryApiService);
  private readonly preferences = inject(NotificationPreferencesApiService);
  private readonly http = inject(HttpClient);
  private readonly session = inject(KeycloakSessionService);
  private readonly url = this.session.adminUrl + '/users';
  private readonly customUrl = this.session.iamUrl + '/users';

  getGrid(
    body: IGridRequestBody,
  ): Observable<IGridResponseBody<IUserGridItem>> {
    return this.registry.search(body);
  }

  getInfo(id: string): Observable<IUser> {
    return forkJoin({
      administration: this.registry.details(id),
      preferences: this.preferences.getUser(id).pipe(
        map((view) => ({ preferences: view.settings, preferencesError: '' })),
        catchError((error) =>
          of({
            preferences: null,
            preferencesError:
              error.status === 403
                ? 'Нет права просмотра настроек уведомлений.'
                : 'Не удалось загрузить настройки уведомлений. Обновите карточку.',
          }),
        ),
      ),
      profile: this.read(id),
      sessions: this.http.get<UserSession[]>(this.userUrl(id) + '/sessions'),
      credentials: this.http.get<UserCredential[]>(
        this.userUrl(id) + '/credentials',
      ),
      loginFailure: this.http
        .get<UserLoginFailure>(
          this.session.adminUrl +
            '/attack-detection/brute-force/users/' +
            encodeURIComponent(id),
        )
        .pipe(
          catchError((error) =>
            error.status === 403 ? of(null) : throwError(() => error),
          ),
        ),
      emailStatus: this.http.get<EmailStatus>(
        this.customUrl + '/' + encodeURIComponent(id) + '/email-status',
      ),
      loginEvents: this.session.isSecurityAdministrator
        ? this.http
            .get<UserLoginEvent[]>(this.session.adminUrl + '/events', {
              params: {
                user: id,
                type: 'LOGIN',
                first: 0,
                max: 2,
                direction: 'desc',
              },
            })
            .pipe(
              catchError((error) =>
                error.status === 403 ? of(null) : throwError(() => error),
              ),
            )
        : of(null),
      deletions: this.deletions([id]),
    }).pipe(
      map(({ deletions, preferences, ...info }) => ({
        ...info,
        ...preferences,
        isSecurityAdministrator:
          info.administration.assignments.securityAdministrator,
        deletedAt: deletions[0]?.deletedAt ?? null,
        deletedBy: deletions[0]?.deletedBy ?? null,
      })),
    );
  }

  createUser(params: IUserUpdateParams) {
    return this.http.post<TemporaryPassword>(this.customUrl, {
      username: params.login,
      email: params.email,
      firstName: params.firstName,
      lastName: params.secondName,
      attributes: this.attributes(params),
      enabled: true,
      assignments: params.assignments,
    });
  }
  updateUser(id: string, params: IUserUpdateParams) {
    return this.http.post<void>(
      this.customUrl + '/' + encodeURIComponent(id) + '/profile',
      {
        firstName: params.firstName,
        lastName: params.secondName,
        email: params.email,
        attributes: this.attributes(params),
        assignments: params.assignments,
      },
    );
  }
  temporaryPassword(id: string) {
    return this.http.post<TemporaryPassword>(
      this.customUrl + '/' + encodeURIComponent(id) + '/temporary-password',
      {},
    );
  }
  logout(id: string) {
    return this.http.post<void>(this.userUrl(id) + '/logout', {});
  }
  requirePasswordChange(id: string) {
    return this.read(id).pipe(
      switchMap((user) =>
        this.updateNativeUser(id, user, {
          requiredActions: [
            ...new Set([...(user.requiredActions ?? []), 'UPDATE_PASSWORD']),
          ],
        }),
      ),
    );
  }
  block(id: string, reason: string) {
    return this.read(id).pipe(
      switchMap((user) =>
        this.updateNativeUser(id, user, {
          enabled: false,
          attributes: { ...user.attributes, blockReason: [reason] },
        }),
      ),
    );
  }
  unblock(id: string) {
    return this.read(id).pipe(
      switchMap((user) =>
        this.updateNativeUser(id, user, {
          enabled: true,
          attributes: { ...user.attributes, blockReason: [] },
        }),
      ),
      switchMap(() =>
        this.http.delete<void>(
          this.session.adminUrl +
            '/attack-detection/brute-force/users/' +
            encodeURIComponent(id),
        ),
      ),
    );
  }
  getList(): Observable<ISelectItem<never, string>[]> {
    const body: IGridRequestBody = {
      page: { pageNumber: 1, pageSize: 10000 },
      complexFilters: [],
      disableCount: true,
      searchString: null,
      sortings: [
        {
          field: 'fullName',
          index: 0,
          direction: 'asc',
        },
      ],
    };

    return this.getGrid(body).pipe(
      map((data) =>
        (data?.items ?? []).map((item) => ({
          key: item.id,
          value: item.fullName,
        })),
      ),
      share(),
    );
  }
  private read(id: string) {
    return this.http.get<KeycloakUser>(this.userUrl(id));
  }
  private userUrl(id: string) {
    return this.url + '/' + encodeURIComponent(id);
  }
  private updateNativeUser(
    id: string,
    user: KeycloakUser,
    changes: Partial<
      Pick<KeycloakUser, 'enabled' | 'requiredActions' | 'attributes'>
    >,
  ) {
    return this.http.put<void>(this.userUrl(id), {
      username: user.username,
      email: user.email,
      firstName: user.firstName,
      lastName: user.lastName,
      enabled: user.enabled,
      emailVerified: user.emailVerified,
      requiredActions: user.requiredActions ?? [],
      attributes: user.attributes ?? {},
      ...changes,
    });
  }
  private deletions(ids: string[]) {
    if (!ids.length) return of<UserDeletion[]>([]);
    const params = ids.reduce(
      (p, id) => p.append('userId', id),
      new HttpParams(),
    );
    return this.http.get<UserDeletion[]>(this.customUrl + '/deletions', {
      params,
    });
  }
  private attributes(params: IUserUpdateParams): Record<string, string[]> {
    return {
      middleName: params.middleName ? [params.middleName] : [],
      phoneNumber: params.phoneNumber ? [params.phoneNumber] : [],
      bskNumber: params.bskNumber ? [params.bskNumber] : [],
      description: params.description ? [params.description] : [],
      allowedIpNetworks: params.allowedIpNetworks,
    };
  }
}
